import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClientService } from '../../../core/services/http-client.service';
import { ToastService } from '../../../core/services/toast.service';
import { CommonService } from '../../../core/services/common.service';

interface Wedge {
  gift: any;
  deg: number;
  thumbnailUrl: string;
}

@Component({
  selector: 'app-spin',
  templateUrl: './spin.component.html',
  styleUrls: ['./spin.component.css']
})
export class SpinComponent implements OnInit, OnDestroy {
  allGifts: any[] = [];
  wedges: Wedge[] = [];
  bulbs: number[] = Array.from({ length: 18 }, (_, i) => i);

  wheelRotation: number = 0;
  isSpinning: boolean = false;
  spunCount: number = 0;

  showResult: boolean = false;
  resultGift: any = null;
  lastWonGift: any = null;
  wonWedgeIndex: number = -1;

  loading: boolean = true;
  currOfferData: any;

  muted: boolean = false;
  private spinAudio: HTMLAudioElement | null = null;
  private winAudio: HTMLAudioElement | null = null;

  private customerId: any;
  private readonly WEDGE_COUNT = 9;
  private readonly DOCUMENT_BASE = 'https://apis.smartdukaan.com/document/';
  private readonly MUTE_KEY = 'spinMuted';

  steps = [
    { n: 1, title: 'Buy a smartphone', desc: 'Visit any participating SmartDukaan store and buy an eligible smartphone during the offer window.' },
    { n: 2, title: 'Get a scratch card', desc: 'Every eligible purchase earns a spin on the Navratri wheel — guaranteed gift.' },
    { n: 3, title: 'Login with your mobile', desc: 'Enter the mobile number used at purchase. We verify it instantly.' },
    { n: 4, title: 'Spin to reveal', desc: 'Tap the wheel to reveal your Navratri gift. One spin per card.' }
  ];

  constructor(
    private http: HttpClientService,
    private toast: ToastService,
    private common: CommonService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.customerId = this.common.getStorage('customer')?.id;
    this.muted = this.common.getStorage(this.MUTE_KEY) === true;
    this.initAudio();
    this.loadGifts();
    this.loadCurrentOffer();
  }

  ngOnDestroy(): void {
    this.stopAudio(this.spinAudio);
    this.stopAudio(this.winAudio);
  }

  private initAudio(): void {
    try {
      this.spinAudio = new Audio('assets/sounds/spin.mp3');
      this.spinAudio.loop = true;
      this.spinAudio.volume = 0.55;
      this.spinAudio.preload = 'auto';
      this.winAudio = new Audio('assets/sounds/win.mp3');
      this.winAudio.volume = 0.85;
      this.winAudio.preload = 'auto';
    } catch {
      // Audio not supported — spin will still work silently.
    }
  }

  private playSpinSound(): void {
    if (this.muted || !this.spinAudio) return;
    try {
      this.spinAudio.currentTime = 0;
      this.spinAudio.play().catch(() => { /* autoplay blocked, ignore */ });
    } catch {}
  }

  private playWinSound(): void {
    this.stopAudio(this.spinAudio);
    if (this.muted || !this.winAudio) return;
    try {
      this.winAudio.currentTime = 0;
      this.winAudio.play().catch(() => { /* autoplay blocked, ignore */ });
    } catch {}
  }

  private stopAudio(a: HTMLAudioElement | null): void {
    if (!a) return;
    try {
      a.pause();
      a.currentTime = 0;
    } catch {}
  }

  toggleMute(): void {
    this.muted = !this.muted;
    this.common.setStorage(this.MUTE_KEY, this.muted);
    if (this.muted) {
      this.stopAudio(this.spinAudio);
      this.stopAudio(this.winAudio);
    }
  }

  private loadGifts(): void {
    this.loading = true;
    this.http.get('/store/giftsByCustomerId/' + this.customerId).subscribe(
      (data) => {
        this.loading = false;
        const gifts = (data?.response || []).filter((g: any) => g.offerName != null);
        this.allGifts = gifts;
        this.buildWedges(gifts);
        this.restoreLastWon(gifts);
      },
      () => {
        this.loading = false;
        this.toast.error('Could not load your gifts');
      }
    );
  }

  private restoreLastWon(gifts: any[]): void {
    // Only auto-restore when the customer has no spins left; otherwise let them spin fresh.
    const activeCount = this.spinsLeft;
    if (activeCount > 0) return;

    // Hide gifts from offers whose claim window has already closed
    // (end_date + scratch_validity + 1 days < today).
    const scratched = gifts
      .filter((g) => g.scratched && g.thumbnailUrl && !this.isGiftExpired(g))
      .sort((a, b) => (b.scratchOfferId || 0) - (a.scratchOfferId || 0));

    if (scratched.length === 0) return;

    this.lastWonGift = scratched[0];
    const key = this.lastWonGift.giftName || this.lastWonGift.offerName;
    const idx = this.wedges.findIndex(
      (w) => (w.gift.giftName || w.gift.offerName) === key
    );
    this.wonWedgeIndex = idx >= 0 ? idx : 0;
  }

  private loadCurrentOffer(): void {
    this.http.get('/store/current-offer').subscribe(
      (data) => {
        if (data?.responseStatus === 'SUCCESS') {
          this.currOfferData = data.response;
        }
      },
      () => {}
    );
  }

  private buildWedges(gifts: any[]): void {
    const unique: any[] = [];
    const seenNames = new Set<string>();
    for (const g of gifts) {
      const key = (g.giftName || g.offerName || '').toString();
      if (!seenNames.has(key) && g.thumbnailUrl) {
        seenNames.add(key);
        unique.push(g);
      }
      if (unique.length >= this.WEDGE_COUNT) break;
    }
    // Pad by repeating entries so the wheel always has WEDGE_COUNT visible slices.
    while (unique.length > 0 && unique.length < this.WEDGE_COUNT) {
      unique.push(unique[unique.length % Math.max(seenNames.size, 1)]);
    }
    const sliceDeg = 360 / this.WEDGE_COUNT;
    this.wedges = unique.slice(0, this.WEDGE_COUNT).map((g, i) => ({
      gift: g,
      deg: i * sliceDeg,
      thumbnailUrl: this.DOCUMENT_BASE + g.thumbnailUrl
    }));
  }

  get activeGift(): any | null {
    return this.allGifts.find(
      (g) => g.status === 'ACTIVE' && !g.scratched && !this.isGiftExpired(g)
    );
  }

  get spinsLeft(): number {
    return this.allGifts.filter(
      (g) => g.status === 'ACTIVE' && !g.scratched && !this.isGiftExpired(g)
    ).length;
  }

  private isGiftExpired(gift: any): boolean {
    if (!gift?.expiredTimestamp) return false;
    const expiry = new Date(gift.expiredTimestamp).getTime();
    return !isNaN(expiry) && expiry < Date.now();
  }

  get hasAnySpin(): boolean {
    return this.spinsLeft > 0;
  }

  spin(): void {
    if (this.isSpinning) return;
    const next = this.activeGift;
    if (!next) {
      this.toast.info('No spins left — make a purchase to earn more!');
      return;
    }
    if (this.wedges.length === 0) return;

    this.isSpinning = true;
    this.playSpinSound();

    const targetIndex = this.wedges.findIndex(
      (w) => (w.gift.giftName || w.gift.offerName) === (next.giftName || next.offerName)
    );
    const landingIndex = targetIndex >= 0 ? targetIndex : 0;
    const sliceDeg = 360 / this.WEDGE_COUNT;
    const fullSpins = 6;

    // Wedge `i` sits at angle `i * sliceDeg` clockwise from the pointer.
    // After rotating the wheel by R clockwise, that wedge is at `(i*sliceDeg + R) mod 360`.
    // We want it at the pointer (0 deg), so R mod 360 must equal `(360 - i*sliceDeg) mod 360`.
    const currentAngle = ((this.wheelRotation % 360) + 360) % 360;
    const targetAngle = ((360 - landingIndex * sliceDeg) % 360 + 360) % 360;
    const forwardDelta = ((targetAngle - currentAngle) + 360) % 360;
    const addRotation = fullSpins * 360 + forwardDelta;

    this.wheelRotation = this.wheelRotation + addRotation;

    setTimeout(() => {
      this.resultGift = next;
      this.lastWonGift = next;
      this.wonWedgeIndex = landingIndex;
      this.markScratched(next);
      this.showResult = true;
      this.isSpinning = false;
      this.spunCount++;
      this.playWinSound();
    }, 5200);
  }

  get isDone(): boolean {
    return !!this.lastWonGift && !this.hasAnySpin;
  }

  seeGift(): void {
    if (!this.lastWonGift) return;
    this.resultGift = this.lastWonGift;
    this.showResult = true;
  }

  handleCta(): void {
    if (this.isSpinning) return;
    if (this.isDone) {
      this.seeGift();
    } else {
      this.spin();
    }
  }

  get customerMobile(): string {
    const c: any = this.common.getStorage('customer');
    const num = (c?.mobileNumber || c?.mobile || '').toString();
    if (num.length !== 10) return num;
    return '+91 ' + num.slice(0, 2) + 'XXXXX' + num.slice(7);
  }

  logout(): void {
    this.common.removeStorage('customer');
    this.common.setLoggedIn(false);
    this.router.navigateByUrl('/festive-login');
  }

  private markScratched(gift: any): void {
    gift.scratched = true;
    gift.status = 'SCRATCHED';
    const id = gift.scratchOfferId || gift.id;
    if (!id) return;
    this.http.get('/store/offer/scratch/' + id).subscribe(
      () => {},
      () => this.toast.error('Could not record your gift. Please refresh.')
    );
  }

  closeResult(): void {
    this.showResult = false;
    this.resultGift = null;
  }

  get claimId(): string {
    const id = this.resultGift?.scratchOfferId || this.resultGift?.id || 0;
    return 'NV-OF-' + id;
  }

  copyClaimId(): void {
    const id = this.claimId;
    const nav: any = typeof navigator !== 'undefined' ? navigator : null;
    if (nav?.clipboard?.writeText) {
      nav.clipboard.writeText(id).then(
        () => this.toast.success('Claim ID copied'),
        () => this.fallbackCopy(id)
      );
    } else {
      this.fallbackCopy(id);
    }
  }

  private fallbackCopy(text: string): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.toast.success('Claim ID copied');
    } catch {
      this.toast.error('Could not copy — please note it manually');
    }
  }

  handleImageError(event: any): void {
    event.target.style.visibility = 'hidden';
  }
}
